import { useRef, type KeyboardEvent } from "react";
import { Check, type LucideIcon } from "lucide-react";

import { mergeClassNames } from "./classNames";
import "./controls.css";

export interface SegmentedControlOption<Value extends string = string> {
  value: Value;
  label: string;
  icon?: LucideIcon;
  disabled?: boolean;
}

export interface SegmentedControlProps<Value extends string = string> {
  label: string;
  options: ReadonlyArray<SegmentedControlOption<Value>>;
  value: Value;
  onChange: (value: Value) => void;
  className?: string;
  labelHidden?: boolean;
}

export function SegmentedControl<Value extends string>({
  label,
  options,
  value,
  onChange,
  className,
  labelHidden = false
}: SegmentedControlProps<Value>) {
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const selectedEnabledIndex = options.findIndex(
    (option) => option.value === value && !option.disabled
  );
  const fallbackIndex = options.findIndex((option) => !option.disabled);
  const tabStopIndex =
    selectedEnabledIndex >= 0 ? selectedEnabledIndex : fallbackIndex;

  function handleKeyDown(
    event: KeyboardEvent<HTMLButtonElement>,
    currentIndex: number
  ) {
    const supportedKeys = [
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End"
    ];

    if (!supportedKeys.includes(event.key)) {
      return;
    }

    const enabledIndices = options.reduce<number[]>((indices, option, index) => {
      if (!option.disabled) {
        indices.push(index);
      }

      return indices;
    }, []);

    if (enabledIndices.length === 0) {
      return;
    }

    const currentPosition = enabledIndices.indexOf(currentIndex);
    let nextPosition: number;

    if (event.key === "Home") {
      nextPosition = 0;
    } else if (event.key === "End") {
      nextPosition = enabledIndices.length - 1;
    } else if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      nextPosition =
        currentPosition < 0 ? 0 : (currentPosition + 1) % enabledIndices.length;
    } else {
      nextPosition =
        currentPosition <= 0
          ? enabledIndices.length - 1
          : currentPosition - 1;
    }

    const nextIndex = enabledIndices[nextPosition];
    const nextOption =
      nextIndex === undefined ? undefined : options[nextIndex];

    if (nextIndex === undefined || !nextOption) {
      return;
    }

    event.preventDefault();
    onChange(nextOption.value);
    buttonRefs.current[nextIndex]?.focus();
  }

  return (
    <fieldset className={mergeClassNames("ui-segmented-control", className)}>
      <legend
        className={mergeClassNames(
          "ui-segmented-control__label",
          labelHidden && "ui-visually-hidden"
        )}
      >
        {label}
      </legend>
      <div
        aria-label={label}
        className="ui-segmented-control__options"
        role="radiogroup"
      >
        {options.map((option, index) => {
          const OptionIcon = option.icon;
          const isSelected = option.value === value;

          return (
            <button
              aria-checked={isSelected}
              className="ui-segmented-control__option"
              disabled={option.disabled}
              key={option.value}
              onClick={() => onChange(option.value)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              ref={(node) => {
                buttonRefs.current[index] = node;
              }}
              role="radio"
              tabIndex={index === tabStopIndex ? 0 : -1}
              type="button"
            >
              {OptionIcon ? (
                <OptionIcon
                  aria-hidden="true"
                  className="ui-segmented-control__option-icon"
                />
              ) : null}
              <span className="ui-segmented-control__option-label">
                {option.label}
              </span>
              <Check
                aria-hidden="true"
                className="ui-segmented-control__check"
              />
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
